<template>
  <div class="col-md-6 mb-3">
    <ControllerInfoCard
      :controller="controllerWithLastVersion"
      :title="'가상스위치 제어기'"
      :room-names="roomDisplayNames"
      badge-mode="auto"
      variant="withRooms"
    >
      <template #headerLeftExtra>
        <div class="form-check" v-if="props.isSelect">
          <input
            class="form-check-input check-controller"
            type="checkbox"
            :value="props.data._id"
            @change="onCheckboxChange"
          />
        </div>
      </template>

      <template #headerRightExtra>
        <div
          v-if="shouldShowDelete"
          @click.stop="onDeleteClick"
          class="controller-delete-btn"
        >
          <i class="fa-regular fa-trash-can"></i>
        </div>
      </template>

      <template #bodyExtra>
        <div class="controller-info-item">
          <div class="controller-info-line">
            <i class="fa-solid fa-plug me-2"></i>
            <span class="controller-info-label">가상장치 연결:</span>
            <span class="controller-info-value virtual-button-connection">
              <select class="form-control form-control-sm virtual-connection-select" v-model="port1VirtualDevice" @change="(e) => onChangeVirtualDevice(e,0)">
                <option value="" disabled>선택</option>
                <option v-for="device in virtualDevices" :key="device._id" :value="device._id" :disabled="device?.virtualMap != undefined && device._id != port1VirtualDevice">
                  {{ device.name }}
                </option>
              </select>
              <select class="form-control form-control-sm virtual-connection-select" v-model="port2VirtualDevice" @change="(e) => onChangeVirtualDevice(e, 1)">
                <option value="" disabled>선택</option>
                <option v-for="device in virtualDevices" :key="device._id" :value="device._id" :disabled="device?.virtualMap != undefined && device._id != port2VirtualDevice">
                  {{ device.name }}
                </option>
              </select>
            </span>
          </div>
        </div>
      </template>
    </ControllerInfoCard>
  </div>
</template>

<script setup>
import { onMounted, ref, defineProps, computed, getCurrentInstance, reactive } from 'vue';
import { useStore } from 'vuex';
import ControllerInfoCard from './ControllerInfoCard.vue';
import Axios from '@/api';
import useMqttControl from '@/common/hooks/useMqttControl';
const mqttControl = reactive(useMqttControl());

const { emit } = getCurrentInstance();
const props = defineProps({
  index: {
    type: Number,
    default: 0,
  },
  data: {
    type: Object,
    required: true,
  },
  isSelect: {
    type: Boolean,
    default: false,
  },
  showDelete: {
    type: Boolean,
    default: null, // null이면 isSelect에 따라 자동 결정
  },
  showRooms: {
    type: Boolean,
    default: true,
  },
});
const store = useStore();

const zone = computed(() => {
  return (
    store.getters['home/getZones']?.find(
      (zone) =>
        zone.controllers.find(
          (controller) => controller._id == props.data._id,
        ),
    ) || undefined
  );
});
const groups = computed(() => {
  return (
    zone.value?.groups.filter((group) =>
      group.devices.some((device) => device.controllerId == props.data._id),
    ) || []
  );
});
const virtualDevices = computed(() => {
  const devices = zone.value?.groups.flatMap((group) => group.devices) || [];
  const virtualDeviceList = devices.filter((device) => device.isVirtual === true);
  const uniqueMap = new Map();
  virtualDeviceList.forEach((device) => {
    if (!uniqueMap.has(device._id)) uniqueMap.set(device._id, device);
  });
  return Array.from(uniqueMap.values());
});

const port1DefaultVirtualDevice = computed(() => {
  return (
    virtualDevices.value.find(
      (d) =>
        d?.virtualMap?.serial == props.data._id && Number(d?.virtualMap?.port) === 0,
    ) || null
  );
});

const port1VirtualDevice = ref(port1DefaultVirtualDevice.value?._id || '');
const port2DefaultVirtualDevice = computed(() => {
  return (
    virtualDevices.value.find(
      (d) =>
        d?.virtualMap?.serial == props.data._id && Number(d?.virtualMap?.port) === 1,
    ) || null
  );
});

const port2VirtualDevice = ref(port2DefaultVirtualDevice.value?._id || '');

const controllerWithLastVersion = computed(() => {
  const base = props.data || {};
  if (!base?.model) return base;
  const products = store.getters['home/getProducts'] || [];
  const product = products.find((p) => p.model === base.model);
  const lastVersion = product?.version;
  return lastVersion !== undefined ? { ...base, lastVersion } : base;
});

onMounted(() => {
  console.log('VirtualControllerItem.onMounted().props =>', props);
});

const onDeleteClick = () => {
  console.log('VirtualControllerItem.onDeleteClick =>', { id: props.data._id });
  emit('onDeleteClick', { id: props.data._id });
};

const onCheckboxChange = (e) => {
  for (const item of document.getElementsByClassName('check-controller')) {
    item.checked = false;
  }
  e.target.checked = true;
  emit('onChecked', e.target.value);
};

const onChangeVirtualDevice = async (e, port) => {
  console.log('VirtualControllerItem.onChangeVirtualDevice =>', { value: e.target.value, port });
  try {
    const virtualDeviceId = e?.target?.value;
    if (!virtualDeviceId) return;

    const result = await Axios.patch(`/devices/${virtualDeviceId}/virtualMap`, {
      virtualMap: { serial: props.data._id, port },
    });

    if (result?.resultCode === '0000') {
      const updatedDevice = result?.resultData;
      const virtualMap = updatedDevice?.virtualMap;

      const zoneGroups = zone.value?.groups || [];
      zoneGroups.forEach((group) => {
        (group.devices || []).forEach((device) => {
          const isSameSerialPort =
            device?.virtualMap?.serial == props.data._id &&
            Number(device?.virtualMap?.port) === Number(port);

          if (device?.isVirtual === true && isSameSerialPort && device?._id != virtualDeviceId) {
            store.commit('home/updateDevice', {
              groupId: group._id,
              id: device._id,
              data: { virtualMap: undefined },
            });
          }
        });
      });

      const targetGroup = zone.value?.groups?.find((group) =>
        (group.devices || []).some((d) => d?._id == virtualDeviceId),
      );

      if (targetGroup?._id && virtualMap != undefined) {
        store.commit('home/updateDevice', {
          groupId: targetGroup._id,
          id: virtualDeviceId,
          data: { virtualMap },
        });
      }

      mqttControl.onPublish(props.data._id, virtualDeviceId, {
        serial: props.data._id,
        cmd: 'vss',
        type: 1,
        data: { port },
      });
    }
  } catch (error) {
    console.error('VirtualControllerItem.onChangeVirtualDevice patchVirtualMap failed:', error);
  }
};

// 삭제 버튼 표시 여부 결정
const shouldShowDelete = computed(() => {
  if (props.showDelete !== null) {
    return props.showDelete;
  }
  // isSelect가 true이면 삭제 버튼 숨김
  return !props.isSelect;
});

// 방 목록 표시 여부 결정
const shouldShowRooms = computed(() => {
  return props.showRooms;
});

/** 펌웨어 업데이트 카드와 동일: 헤더 아래 방 이름 태그 */
const roomDisplayNames = computed(() => {
  if (!shouldShowRooms.value) return [];
  return (groups.value || [])
    .map((g) => g?.name)
    .filter((n) => typeof n === 'string' && n.length > 0);
});
</script>

<style scoped>
.controller-delete-btn {
  cursor: pointer;
  padding: 0.5rem;
  color: #6c757d;
  transition: all 0.2s ease;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
}

.controller-delete-btn:hover {
  background-color: #f8f9fa;
  color: #212529;
}

.controller-info-value span {
  font: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
  vertical-align: baseline;
}

.virtual-button-connection {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
}

.virtual-connection-select {
  flex: 1;
  min-width: 0;
}
</style>

